import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { meetingsApi, financialRecordsApi } from "../../services/api";
import {
  CalendarEvent,
  CalendarEventType,
  CalendarEventTypeKey,
  normalizeToCalendarEvents,
  countEventsByType,
} from "../../utils/calendarUtils";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import CalendarFilters from "../../components/calendar/CalendarFilters";
import CalendarViewSwitcher, {
  type CalendarView,
} from "../../components/calendar/CalendarViewSwitcher";
import MonthView from "../../components/calendar/MonthView";
import WeekView from "../../components/calendar/WeekView";
import DayView from "../../components/calendar/DayView";
import YearView from "../../components/calendar/YearView";
import UpcomingHitList from "../../components/calendar/UpcomingHitList";

const ALL_FILTERS: CalendarEventTypeKey[] = [
  CalendarEventType.Meeting,
  CalendarEventType.Invoice,
  CalendarEventType.Bill,
  CalendarEventType.Payment,
];

const CalendarPage: React.FC = () => {
  const { currentBuilding } = useBuilding();
  const buildingId = currentBuilding?.id || "";

  const [currentView, setCurrentView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeFilters, setActiveFilters] =
    useState<CalendarEventTypeKey[]>(ALL_FILTERS);
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError(null);

    try {
      const [meetings, financialRecords] = await Promise.all([
        meetingsApi.getAll(buildingId).catch(() => []),
        financialRecordsApi.getAll(buildingId).catch(() => []),
      ]);

      const events = normalizeToCalendarEvents(
        meetings || [],
        financialRecords || [],
      );
      setAllEvents(events);
    } catch (err) {
      setError("Failed to load calendar events. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = useMemo(
    () => allEvents.filter((e) => activeFilters.includes(e.type)),
    [allEvents, activeFilters],
  );

  const eventCounts = useMemo(() => countEventsByType(allEvents), [allEvents]);

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setCurrentView("day");
  };

  const handleMonthClick = (date: Date) => {
    setCurrentDate(date);
    setCurrentView("month");
  };

  const renderView = () => {
    switch (currentView) {
      case "year":
        return (
          <YearView
            currentDate={currentDate}
            events={filteredEvents}
            onMonthClick={handleMonthClick}
          />
        );
      case "month":
        return (
          <MonthView
            currentDate={currentDate}
            events={filteredEvents}
            onDayClick={handleDayClick}
          />
        );
      case "week":
        return (
          <WeekView
            currentDate={currentDate}
            events={filteredEvents}
            onDayClick={handleDayClick}
          />
        );
      case "day":
        return <DayView currentDate={currentDate} events={filteredEvents} />;
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Calendar"
          description="View meetings, invoices, expenses and payments across time"
        />
        <div className="mt-8 flex justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader
          title="Calendar"
          description="View meetings, invoices, expenses and payments across time"
        />
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Calendar"
        description="View meetings, invoices, expenses and payments across time"
      />

      <div className="mt-4 flex flex-col gap-4 lg:flex-row">
        {/* Sidebar — Upcoming Hit List */}
        <div className="w-full flex-shrink-0 lg:w-72">
          <UpcomingHitList events={filteredEvents} />
        </div>

        {/* Main calendar area */}
        <div className="min-w-0 flex-1">
          {/* Controls */}
          <div className="mb-4 space-y-3">
            <CalendarViewSwitcher
              currentView={currentView}
              currentDate={currentDate}
              onViewChange={setCurrentView}
              onDateChange={setCurrentDate}
            />
            <CalendarFilters
              activeFilters={activeFilters}
              onFilterChange={setActiveFilters}
              eventCounts={eventCounts}
            />
          </div>

          {/* Calendar view */}
          {renderView()}
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
